import classNames from 'classnames';
import {
  ChangeEventHandler,
  MouseEventHandler,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useClickOutside, useGetAllProvidersCached, useWindowSize } from 'react-easyrocket';
import { useTranslation } from 'react-i18next';
import noProviders from 'src/assets/images/plugs/noProviders.webp';
import providerDefault from 'src/assets/images/providerDefault.svg';
import { useDeviceDetect } from 'src/hooks/useDeviceDetect';
import { Button } from 'src/components/Buttons/Button';
import { ProviderButton } from 'src/components/Buttons/ProviderButton';
import { Input } from 'src/components/Inputs/Input';
import { InputSearch } from 'src/components/Inputs/InputSearch/InputSearch';
import { Arrow } from 'src/components/svg/Arrows';
import { Spin } from 'src/components/svg/General';
import { ProviderSearchIcon } from 'src/components/svg/ProviderSearchIcon';
import { SearchIconThin } from 'src/components/svg/Searches';
import { STORAGE_URL } from 'src/api';
import '../../styles.css';
import { useModalContext } from 'src/hooks/useModalContext';
import { useGetProviderFromUrl } from '../../hooks/useGetProviderFromUrl';

interface InputWithButtonProps {
  onChange?: (value: string) => void;
  value?: string;
}

const _GamesAndProvidersSearch = ({ onChange, value }: InputWithButtonProps) => {
  const { isDekstopLayout, is768 } = useWindowSize();
  const { openModal } = useModalContext();
  const providersSearchRef = useRef<HTMLDivElement | null>(null);
  const [gamesSearchValue, setGamesSearchValue] = useState(value || '');
  const [providersSearchValue, setProvidersSearchValue] = useState<string>('');
  const [showProviders, setShowProviders] = useState(false);
  const providerSearchRef = useRef<HTMLDivElement>(null);
  const providersToggle = useRef<HTMLButtonElement>(null);
  const { providerTitle } = useGetProviderFromUrl();
  const { isIos } = useDeviceDetect();

  const { t } = useTranslation();
  const { providers: allProviders, isFetching, isFetched } = useGetAllProvidersCached();

  useEffect(() => {
    if (value !== undefined) {
      setGamesSearchValue(value);
    }
  }, [value]);

  const handleGameSearchChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    setGamesSearchValue(e.target.value);
    onChange?.(e.target.value);
  };

  const scrollToProviders = useCallback(() => {
    providersSearchRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [providersSearchRef]);

  const handleChangeProviders = useCallback((value: string) => {
    setProvidersSearchValue(value);
  }, []);

  const handleProviderSwitchClick: MouseEventHandler<HTMLButtonElement> = () => {
    if (is768) {
      setShowProviders((showProviders) => !showProviders);
      setProvidersSearchValue('');
      !showProviders && scrollToProviders();
    } else {
      openModal('SEARCH', { props: { searchTab: 'providers' } });
    }
  };

  const handleProviderSelect = () => {
    setGamesSearchValue('');
    onChange?.('');
  };

  const providers = useMemo(
    () =>
      allProviders.filter((provider) =>
        provider.name.toLowerCase().includes(providersSearchValue.toLowerCase())
      ),
    [providersSearchValue, allProviders]
  );

  const activeProvider = useMemo(() => {
    if (!providers) return null;
    return providers.find(({ name }) => name === providerTitle);
  }, [providerTitle, providers]);

  useClickOutside(providerSearchRef, () => {
    isDekstopLayout && setShowProviders(false);
  }, [providersToggle]);

  return (
    <div ref={providersSearchRef} className="relative flex flex-col items-center scroll-mt-[100px]">
      <div className="flex w-full landscape:w-[98%] gap-[10px] items-center flex-col md:flex-row relative z-20">
        <Input
          value={gamesSearchValue}
          onClick={() => !isIos && scrollToProviders()}
          onChange={handleGameSearchChange}
          leftIcon={<SearchIconThin className="pr-[8px]" />}
          // overrideContainerClassName="gap-0 w-full"
          placeholder={t('general.placeholderSearchGames') as string}
        />
        <Button
          mode="button-primary"
          title={`${t('general.providerList')}`}
          className="max-w-full md:max-w-[285px] text-[15px] py-3 px-[30px]"
          ref={providersToggle}
          onClick={handleProviderSwitchClick}>
          <div className="flex justify-between items-center w-full">
            <div className="flex items-center gap-2">
              {activeProvider ? (
                <img
                  src={STORAGE_URL + activeProvider.providerIcon}
                  onError={(event) => {
                    const element = event.target as HTMLImageElement;
                    element.src = providerDefault;
                    element.onerror = null;
                  }}
                  alt="active provider icon"
                  className="w-[24px]"
                />
              ) : (
                <ProviderSearchIcon />
              )}
              {`${activeProvider ? activeProvider.name.toUpperCase() : t('general.providerList')}`}
            </div>
            <Arrow
              fill="var(--p_arrow)"
              className={classNames(
                'w-[20px] stroke-[3px] transition-transform',
                showProviders ? 'rotate-180' : ''
              )}
            />
          </div>
        </Button>
      </div>

      {showProviders && (
        <div
          ref={providerSearchRef}
          className="relative md:menuShadow md:absolute shadow-[var(--p_shadow)] w-full md:w-[calc(100%+40px)] landscape:w-full md:px-5 landscape:left-0 pt-[10px] md:pt-[70px] pb-[20px]  top-0 md:top-[-10px] left-0 md:left-[-20px] z-10 bg-download_sidebar rounded-[20px]">
          <div className="w-full mb-[30px]">
            <InputSearch
              onClick={scrollToProviders}
              placeholder={`${t('general.searchProvider')}`}
              value={providersSearchValue}
              onChange={handleChangeProviders}
            />
          </div>
          <div
            className={`${
              isFetching && !isFetched
                ? 'flex items-center justify-center w-full h-full'
                : 'w-full grid grid-cols-2 md:grid-cols-4 gap-2.5 grid-rows-[34px] overflow-y-scroll max-h-[360px]'
            }`}>
            {providers?.map((provider) => (
              <div
                className="w-full"
                key={provider.id}
                onClick={() => {
                  setShowProviders((showProviders) => !showProviders);
                }}>
                <ProviderButton provider={provider} onClick={handleProviderSelect} />
              </div>
            ))}
            {isFetching && !isFetched && (
              <div className="h-full w-full flex flex-col items-center mt-5">
                <Spin className="w-[30px] h-[30px]" />
              </div>
            )}
            <div className="h-[20px]"></div>
          </div>
          {!isFetching && providers?.length === 0 && (
            <div className="flex flex-col justify-start items-center w-full h-full">
              <p className="text-p_name w-full text-center">{t('general.noProvidersFound')}</p>
              <img src={noProviders} alt="no providers" className="max-h-[80%] max-w-[300px]" />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// eslint-disable-next-line no-restricted-exports
export const GamesAndProvidersSearch = memo(_GamesAndProvidersSearch);
